import { useEffect, useRef, useState } from "react";
import { Box, LinearProgress, linearProgressClasses, SxProps, Theme } from "@mui/material";
import { keyframes } from "@mui/system";

// ——— Holo progress bar ———
// The remaining-TR3 bar shared by the LandPage Mintable field and the Mintable
// table column: a black track, a "holo" fill whose gradient keeps sliding, and a
// white dot on the fill's end. With `fillIn` the bar also fills from empty once,
// and the dot fades in as the fill's edge reaches it.

// Colours slide right-to-left through the fill forever. Compositor-only: the
// gradient sits on a ::before strip four bar-widths wide, tiled every two (one
// gradient period), and translates one period per loop. The seam never shows
// because the gradient starts and ends on the same green. Animating
// background-position instead would repaint every bar on every frame, forever,
// once per table row.
const holoSlide = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
`;

// One-shot fill. Only a "from" frame on purpose: the bar animates to whatever
// translateX MUI set inline for the current value, so the keyframe never needs
// the percentage.
const holoFillIn = keyframes`
  from { transform: translateX(-101%); }
`;

// The end-dot's fade. Opacity only; the natural style is the 100% frame, so it
// runs `backwards` and leaves nothing behind once done.
const holoDotIn = keyframes`
  from { opacity: 0; }
`;

const HOLO_GRADIENT =
  "linear-gradient(90deg, #2B3501 0%, #4a6a0a 10%, #6da832 20%, #BC92F8 38%, #9B6BE8 48%, " +
  "#D1EFFF 58%, #78CFFF 68%, #BC92F8 78%, #4a6a0a 90%, #2B3501 100%)";

// Fill timing. The ease must keep y1 = y2 = 1: fillArrivalMs relies on it.
const FILL_MS = 1000;
const FILL_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const DOT_FADE_MS = 250;

// When (ms after the fill is started, its delay included) the fill's leading
// edge reaches the dot at `value`%. The edge travels from -1% to value%, so it
// meets the dot (centred at value - 1%) at eased progress p = value / (value + 1).
// With y1 = y2 = 1 the curve's y(s) = 1 - (1 - s)³ inverts exactly,
// s = 1 - ∛(1 - p), and x(s) is the elapsed fraction of the fill.
const fillArrivalMs = (value: number, delayMs: number) => {
  if (!(value > 0)) {
    return delayMs;
  }
  const s = 1 - Math.cbrt(1 / (value + 1));
  const x = 3 * (1 - s) ** 2 * s * 0.16 + 3 * (1 - s) * s ** 2 * 0.3 + s ** 3;
  return delayMs + Math.round(x * FILL_MS);
};

const reducedMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

type HoloProgressBarProps = {
  /** Remaining share, 0–100. */
  value: number;
  /** Fill from empty once the bar is first in view, the dot fading in on arrival. */
  fillIn?: boolean;
  /** Wait before the fill starts, for sequencing it after a surrounding entrance. */
  fillDelayMs?: number;
  "aria-label"?: string;
  /** Wrapper overrides (spacing etc.). */
  sx?: SxProps<Theme>;
};

const HoloProgressBar = ({
  value,
  fillIn = false,
  fillDelayMs = 0,
  "aria-label": ariaLabel,
  sx,
}: HoloProgressBarProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  // The fill and the dot's fade are declared from mount but held paused on their
  // `from` frames until the bar is in view, so a bar below the fold (most table
  // rows) doesn't spend its reveal where nobody can see it. Any visible pixel
  // counts: a stricter threshold could leave a bar that's always partly clipped
  // (a side-scrolled mobile table) empty forever.
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!fillIn || !el || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    if (!("IntersectionObserver" in window)) {
      setPlaying(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setPlaying(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fillIn]);

  return (
    <Box
      ref={rootRef}
      sx={[
        {
          position: "relative",
          width: "100%",
          // Beats the `animation` shorthands below on specificity, so the
          // longhand holds both one-shots until the bar is in view.
          [`&:not([data-playing]) .${linearProgressClasses.bar}, &:not([data-playing]) .holo-dot`]: {
            animationPlayState: "paused",
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      data-playing={playing || undefined}
    >
      <LinearProgress
        variant="determinate"
        value={value}
        aria-label={ariaLabel}
        sx={(theme) => ({
          height: 10,
          width: "100%",
          borderRadius: "20px",
          backgroundColor: "black",
          ...theme.applyStyles("dark", { backgroundColor: theme.palette.grey[800] }),
          [`& .${linearProgressClasses.bar}`]: {
            borderRadius: "5px",
            overflow: "hidden",
            ...(fillIn
              ? { animation: `${holoFillIn} ${FILL_MS}ms ${FILL_EASE} ${fillDelayMs}ms both`, ...reducedMotion }
              : {}),
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "400%",
              background: HOLO_GRADIENT,
              backgroundSize: "50% 100%",
              animation: `${holoSlide} 2.8s linear infinite`,
              ...reducedMotion,
            },
          },
        })}
      />
      <Box
        className="holo-dot"
        sx={{
          position: "absolute",
          top: "50%",
          left: `${value - 1}%`,
          transform: "translate(-50%, -50%)",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: "white",
          pointerEvents: "none",
          // Hidden until the fill's edge arrives, then fades in on top of it.
          ...(fillIn
            ? {
                animation: `${holoDotIn} ${DOT_FADE_MS}ms ease-out ${fillArrivalMs(value, fillDelayMs)}ms backwards`,
                ...reducedMotion,
              }
            : {}),
        }}
      />
    </Box>
  );
};

export default HoloProgressBar;
